import React, { useState, useEffect, memo } from 'react';
import { Form, Input, Select, Card, Spin, message, Row, Col, Radio } from 'antd';
import { Button } from 'components';
import { SaveOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useFooterMenus from '../useFooterMenus';
import ErrorHelper from 'helpers/ErrorHelper';

const { Option } = Select;

export const FooterMenuForm = memo(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedShoppingCenter, setSelectedShoppingCenter] = useState(null);
  const {
    shoppingCenters,
    loadingShoppingCenters,
    createFooterMenu,
    updateFooterMenu,
    fetchFooterMenu,
  } = useFooterMenus();

  const isEditMode = !!id;
  const menuType = Form.useWatch('menu_type', form);

  const getAvailableLanguages = () => {
    if (!selectedShoppingCenter) return ['IT', 'EN'];

    const isTorino =
      selectedShoppingCenter.title?.toLowerCase().includes('torino') ||
      selectedShoppingCenter.name?.toLowerCase().includes('torino') ||
      selectedShoppingCenter.id === 'torino';

    return isTorino ? ['IT', 'EN', 'FR'] : ['IT', 'EN'];
  };

  const availableLanguages = getAvailableLanguages();

  useEffect(() => {
    if (isEditMode) {
      const loadFooterMenu = async () => {
        setLoading(true);
        try {
          const footerMenu = await fetchFooterMenu(id);
          if (footerMenu) {
            setSelectedShoppingCenter(footerMenu.shopping_center);

            form.setFieldsValue({
              title_it: footerMenu.title_it,
              title_en: footerMenu.title_en,
              title_fr: footerMenu.title_fr,
              menu_value_it: footerMenu.menu_value_it,
              menu_value_en: footerMenu.menu_value_en,
              menu_value_fr: footerMenu.menu_value_fr,
              menu_type: footerMenu.menu_type || 'text',
              open_in_new_tab: !!footerMenu.open_in_new_tab,
              shopping_center_id: footerMenu.shopping_center?.id,
            });
          }
        } catch (error) {
          console.error('Error loading footer menu:', error);
          message.error('Failed to load footer menu');
        } finally {
          setLoading(false);
        }
      };

      loadFooterMenu();
    }
  }, [id, isEditMode, fetchFooterMenu, form]);

  const handleSubmit = async (values) => {
    setSubmitting(true);

    try {
      const formData = {
        title_it: values.title_it,
        title_en: values.title_en,
        title_fr: values.title_fr,
        menu_value_it: values.menu_value_it,
        menu_value_en: values.menu_value_en,
        menu_value_fr: values.menu_value_fr,
        menu_type: values.menu_type,
        open_in_new_tab:
          values.menu_type === 'link' ? !!values.open_in_new_tab : false,
        shopping_center_id: values.shopping_center_id,
      };

      const result = isEditMode
        ? await updateFooterMenu(id, formData)
        : await createFooterMenu(formData);

      if (result) {
        navigate('/website/footer_menus');
      }
    } catch (error) {
      console.error('Error saving footer menu:', error);
      ErrorHelper.applyToForm(form, error, t('Failed to save footer menu'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && isEditMode) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size='large' />
      </div>
    );
  }

  return (
    <div>
      <Card
        title={isEditMode ? t('Modifica Footer menu') : t('Crea Nuovo Footer menu')}
        extra={
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/website/footer_menus')}
          >
            {t('Indietro')}
          </Button>
        }
      >
        <Form
          form={form}
          layout='vertical'
          onFinish={handleSubmit}
          scrollToFirstError
          initialValues={{
            menu_type: 'text',
            open_in_new_tab: false,
          }}
        >
          <Form.Item
            name='shopping_center_id'
            label={t('Outlet Village')}
            rules={[
              { required: true, message: t('Seleziona un Outlet Village') },
            ]}
          >
            <Select
              placeholder={t('Seleziona Outlet Village')}
              loading={loadingShoppingCenters}
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              onChange={(value) => {
                const center = shoppingCenters.find(
                  (c) => (c.value || c.id) === value,
                );
                setSelectedShoppingCenter(center);
              }}
            >
              {shoppingCenters
                .filter(
                  (center) =>
                    center &&
                    (center.value || center.id) &&
                    (center.title || center.name),
                )
                .map((center) => (
                  <Option
                    key={center.value || center.id}
                    value={center.value || center.id}
                  >
                    {center.title || center.name}
                  </Option>
                ))}
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={availableLanguages.length === 3 ? 8 : 12}>
              <Form.Item
                name='title_it'
                label={`${t('Titolo')} IT`}
                rules={[
                  { required: true, message: t('Inserisci il titolo') },
                ]}
              >
                <Input
                  placeholder={t('Inserisci il titolo')}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={availableLanguages.length === 3 ? 8 : 12}>
              <Form.Item name='title_en' label={`${t('Titolo')} EN`}>
                <Input
                  placeholder={t('Inserisci il titolo')}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            {availableLanguages.includes('FR') && (
              <Col span={8}>
                <Form.Item name='title_fr' label={`${t('Titolo')} FR`}>
                  <Input
                    placeholder={t('Inserisci il titolo')}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            )}
          </Row>

          <Form.Item
            name='menu_type'
            label={t('Tipo')}
            rules={[{ required: true, message: t('Seleziona un tipo') }]}
          >
            <Radio.Group>
              <Radio value='text'>{t('Testo')}</Radio>
              <Radio value='link'>{t('Link')}</Radio>
            </Radio.Group>
          </Form.Item>

          {menuType === 'link' && (
            <Form.Item
              name='open_in_new_tab'
              label={t('Aprire nella nuova scheda?')}
              rules={[
                { required: true, message: t('Seleziona un opzione') },
              ]}
            >
              <Radio.Group>
                <Radio value={true}>{t('Sì')}</Radio>
                <Radio value={false}>{t('No')}</Radio>
              </Radio.Group>
            </Form.Item>
          )}

          <Row gutter={16}>
            <Col span={availableLanguages.length === 3 ? 8 : 12}>
              <Form.Item
                name='menu_value_it'
                label={`${t('Valore')} IT`}
                rules={[
                  { required: true, message: t('Inserisci il valore') },
                ]}
              >
                <Input
                  placeholder={
                    menuType === 'link'
                      ? t('Inserisci il link')
                      : t('Inserisci il testo')
                  }
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={availableLanguages.length === 3 ? 8 : 12}>
              <Form.Item name='menu_value_en' label={`${t('Valore')} EN`}>
                <Input
                  placeholder={
                    menuType === 'link'
                      ? t('Inserisci il link')
                      : t('Inserisci il testo')
                  }
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            {availableLanguages.includes('FR') && (
              <Col span={8}>
                <Form.Item name='menu_value_fr' label={`${t('Valore')} FR`}>
                  <Input
                    placeholder={
                      menuType === 'link'
                        ? t('Inserisci il link')
                        : t('Inserisci il testo')
                    }
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            )}
          </Row>
        </Form>

        <div style={{ marginTop: 24, textAlign: 'right' }}>
          <Button
            type='primary'
            icon={<SaveOutlined />}
            onClick={() => form.submit()}
            loading={submitting}
            size='large'
          >
            {t('Salva')}
          </Button>
        </div>
      </Card>
    </div>
  );
});

export default FooterMenuForm;
